/**
 * ParticleEffects - Advanced particle system for visual effects
 */
class ParticleEffects {
  constructor(containerElement) {
    this.container = containerElement;
    this.particles = [];
    this.animationId = null;
    this.isRunning = false;
  }

  // Start ambient floating particles
  startAmbientParticles(count = 30) {
    this.stopAmbientParticles();
    this.isRunning = true;
    
    // Create particles
    for (let i = 0; i < count; i++) {
      const particle = document.createElement('div');
      particle.className = 'ambient-particle';
      particle.style.cssText = `
        position: fixed;
        width: ${Math.random() * 4 + 2}px;
        height: ${Math.random() * 4 + 2}px;
        background: radial-gradient(circle, rgba(217, 119, 6, ${Math.random() * 0.5 + 0.2}), transparent);
        border-radius: 50%;
        left: ${Math.random() * 100}vw;
        top: ${Math.random() * 100}vh;
        pointer-events: none;
        z-index: 1;
        animation: float-${Math.floor(Math.random() * 3)} ${Math.random() * 10 + 15}s linear infinite;
        opacity: ${Math.random() * 0.6 + 0.2};
        filter: blur(${Math.random() * 1}px);
      `;
      this.container.appendChild(particle);
      this.particles.push(particle);
    }
  }

  stopAmbientParticles() {
    this.particles.forEach(p => p.remove());
    this.particles = [];
    this.isRunning = false;
  }

  // Firework explosion
  triggerFirework(x = 50, y = 50) {
    const fireworkCount = 50;
    const colors = ['#d97706', '#f59e0b', '#fbbf24', '#e8dcc4', '#ff6b6b', '#ef4444'];
    
    for (let i = 0; i < fireworkCount; i++) {
      const particle = document.createElement('div');
      const angle = (Math.PI * 2 * i) / fireworkCount;
      const velocity = Math.random() * 200 + 150;
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      particle.className = 'firework-particle';
      particle.style.cssText = `
        position: fixed;
        width: 6px;
        height: 6px;
        background: ${color};
        border-radius: 50%;
        left: ${x}vw;
        top: ${y}vh;
        pointer-events: none;
        z-index: 9999;
        box-shadow: 0 0 10px ${color}, 0 0 20px ${color};
      `;
      
      this.container.appendChild(particle);
      
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;
      
      this.animateFireworkParticle(particle, vx, vy);
    }
  }

  animateFireworkParticle(particle, vx, vy) {
    let x = parseFloat(particle.style.left);
    let y = parseFloat(particle.style.top);
    let opacity = 1;
    const gravity = 0.5;
    const friction = 0.99;
    let velocityX = vx / 100;
    let velocityY = vy / 100;
    
    const animate = () => {
      velocityY += gravity / 100;
      velocityX *= friction;
      velocityY *= friction;
      
      x += velocityX;
      y += velocityY;
      opacity -= 0.015;
      
      particle.style.left = x + 'vw';
      particle.style.top = y + 'vh';
      particle.style.opacity = opacity;
      
      if (opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        particle.remove();
      }
    };
    
    requestAnimationFrame(animate);
  }

  // ULTRA REALISTIC water drop effect
  triggerLiquidDrip(element) {
    const rect = element.getBoundingClientRect();
    const dropCount = 2; // Realistic drop count
    
    for (let i = 0; i < dropCount; i++) {
      setTimeout(() => {
        const drip = document.createElement('div');
        const offsetX = (Math.random() - 0.5) * rect.width * 0.4;
        const startX = rect.left + rect.width / 2 + offsetX;
        const startY = rect.top + rect.height;
        
        drip.className = 'realistic-water-drop';
        drip.style.cssText = `
          position: fixed;
          left: ${startX}px;
          top: ${startY}px;
          width: 8px;
          height: 12px;
          background: radial-gradient(ellipse at 30% 30%, 
            rgba(232, 220, 196, 0.95), 
            rgba(232, 220, 196, 0.7) 60%,
            rgba(232, 220, 196, 0.4) 100%);
          border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
          pointer-events: none;
          z-index: 9999;
          box-shadow: 
            inset -2px -2px 4px rgba(255, 255, 255, 0.6),
            inset 2px 2px 4px rgba(0, 0, 0, 0.3),
            0 2px 6px rgba(0, 0, 0, 0.4);
          filter: blur(0.3px);
        `;
        
        this.container.appendChild(drip);
        
        // Realistic falling animation with physics
        let y = 0;
        let velocity = 0;
        const gravity = 0.6;
        const maxDistance = 80;
        let lastTime = performance.now();
        
        const fall = (currentTime) => {
          const deltaTime = (currentTime - lastTime) / 16.67; // Normalize to 60fps
          lastTime = currentTime;
          
          velocity += gravity * deltaTime;
          y += velocity * deltaTime;
          
          // Teardrop shape changes as it falls
          const progress = y / maxDistance;
          const stretch = 1 + progress * 0.8;
          
          drip.style.top = (startY + y) + 'px';
          drip.style.transform = `scaleY(${stretch}) scaleX(${1 / Math.sqrt(stretch)})`;
          drip.style.opacity = 1 - (progress * 0.3);
          drip.style.borderRadius = `${50 - progress * 10}% ${50 - progress * 10}% 50% 50% / ${40 - progress * 10}% ${40 - progress * 10}% ${60 + progress * 20}% ${60 + progress * 20}%`;
          
          if (y < maxDistance) {
            requestAnimationFrame(fall);
          } else {
            this.createRealisticSplash(startX, startY + maxDistance);
            drip.remove();
          }
        };
        
        requestAnimationFrame(fall);
      }, i * 150);
    }
  }

  createRealisticSplash(x, y) {
    // Main splash circle
    const mainSplash = document.createElement('div');
    mainSplash.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      width: 4px;
      height: 4px;
      background: radial-gradient(circle, rgba(232, 220, 196, 0.8), transparent);
      border-radius: 50%;
      pointer-events: none;
      z-index: 9999;
    `;
    this.container.appendChild(mainSplash);
    
    mainSplash.animate([
      { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.8 },
      { transform: 'translate(-50%, -50%) scale(8)', opacity: 0 }
    ], {
      duration: 400,
      easing: 'ease-out'
    }).onfinish = () => mainSplash.remove();
    
    // Tiny droplets
    const dropletCount = 8;
    for (let i = 0; i < dropletCount; i++) {
      const droplet = document.createElement('div');
      const angle = (Math.PI * 2 * i) / dropletCount;
      const distance = Math.random() * 25 + 15;
      
      droplet.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        width: 3px;
        height: 3px;
        background: radial-gradient(circle, rgba(232, 220, 196, 0.9), rgba(232, 220, 196, 0.4));
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      `;
      
      this.container.appendChild(droplet);
      
      // Parabolic arc motion
      let time = 0;
      const duration = 0.5;
      const vx = Math.cos(angle) * distance * 2;
      const vy = Math.sin(angle) * distance * 2 - 20; // Initial upward velocity
      const gravity = 60;
      
      const animateDroplet = () => {
        time += 0.016; // ~60fps
        
        const currentX = x + vx * time;
        const currentY = y + vy * time + 0.5 * gravity * time * time;
        
        droplet.style.left = currentX + 'px';
        droplet.style.top = currentY + 'px';
        droplet.style.opacity = 1 - (time / duration);
        droplet.style.transform = `scale(${1 - time / duration})`;
        
        if (time < duration) {
          requestAnimationFrame(animateDroplet);
        } else {
          droplet.remove();
        }
      };
      
      requestAnimationFrame(animateDroplet);
    }
  }

  // Melt effect
  triggerMelt(element) {
    const overlay = document.createElement('div');
    const rect = element.getBoundingClientRect();
    
    overlay.className = 'melt-overlay';
    overlay.style.cssText = `
      position: fixed;
      left: ${rect.left}px;
      top: ${rect.top}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      background: linear-gradient(to bottom, 
        rgba(232, 220, 196, 0) 0%,
        rgba(232, 220, 196, 0.3) 50%,
        rgba(232, 220, 196, 0.6) 100%);
      pointer-events: none;
      z-index: 9999;
      border-radius: 6px;
      animation: melt-down 1s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    `;
    
    this.container.appendChild(overlay);
    setTimeout(() => overlay.remove(), 1000);
  }

  // Electric sparks
  triggerElectricSparks(x, y, count = 10) {
    for (let i = 0; i < count; i++) {
      const spark = document.createElement('div');
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 100 + 50;
      
      spark.className = 'electric-spark';
      spark.style.cssText = `
        position: fixed;
        left: ${x}vw;
        top: ${y}vh;
        width: 2px;
        height: ${Math.random() * 20 + 10}px;
        background: linear-gradient(to bottom, #fbbf24, transparent);
        pointer-events: none;
        z-index: 9999;
        transform: rotate(${angle}rad);
        box-shadow: 0 0 10px #fbbf24, 0 0 20px #f59e0b;
        animation: spark-${i} ${Math.random() * 0.3 + 0.2}s ease-out forwards;
      `;
      
      this.container.appendChild(spark);
      
      const keyframes = `
        @keyframes spark-${i} {
          0% { opacity: 1; transform: rotate(${angle}rad) translateY(0); }
          100% { opacity: 0; transform: rotate(${angle}rad) translateY(${distance}px); }
        }
      `;
      
      const style = document.createElement('style');
      style.textContent = keyframes;
      document.head.appendChild(style);
      
      setTimeout(() => {
        spark.remove();
        style.remove();
      }, 500);
    }
  }

  // Glow pulse wave
  triggerGlowPulse(element) {
    const pulse = document.createElement('div');
    const rect = element.getBoundingClientRect();
    
    pulse.className = 'glow-pulse';
    pulse.style.cssText = `
      position: fixed;
      left: ${rect.left - 10}px;
      top: ${rect.top - 10}px;
      width: ${rect.width + 20}px;
      height: ${rect.height + 20}px;
      border: 2px solid rgba(217, 119, 6, 0.6);
      border-radius: 8px;
      pointer-events: none;
      z-index: 9998;
      animation: pulse-expand 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    `;
    
    this.container.appendChild(pulse);
    setTimeout(() => pulse.remove(), 800);
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes float-0 {
    0%, 100% { transform: translate(0, 0); }
    25% { transform: translate(20px, -30px); }
    50% { transform: translate(-15px, -60px); }
    75% { transform: translate(10px, -40px); }
  }
  
  @keyframes float-1 {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(-25px, -35px) rotate(45deg); }
    66% { transform: translate(20px, -70px) rotate(-30deg); }
  }
  
  @keyframes float-2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    40% { transform: translate(15px, -45px) scale(1.2); }
    80% { transform: translate(-20px, -55px) scale(0.8); }
  }
  
  @keyframes water-drop-fall {
    0% { 
      transform: translateY(0) scale(1); 
      opacity: 1; 
    }
    20% {
      transform: translateY(15px) scale(1.1);
      opacity: 0.9;
      border-radius: 50% 50% 50% 50%;
    }
    50% { 
      transform: translateY(35px) scale(1, 1.3); 
      opacity: 0.8;
      border-radius: 50% 50% 40% 40%;
    }
    80% {
      transform: translateY(55px) scale(0.8, 1.5);
      opacity: 0.5;
      border-radius: 45% 45% 35% 35%;
    }
    100% { 
      transform: translateY(70px) scale(0.5, 1.8); 
      opacity: 0;
      border-radius: 40% 40% 30% 30%;
    }
  }
  
  @keyframes drip-fall {
    0% { height: 0; opacity: 1; }
    50% { height: 40px; opacity: 0.8; }
    100% { height: 60px; opacity: 0; transform: translateY(20px); }
  }
  
  @keyframes melt-down {
    0% { transform: translateY(0) scaleY(1); opacity: 1; }
    100% { transform: translateY(100%) scaleY(2); opacity: 0; }
  }
  
  @keyframes pulse-expand {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
  }
`;
document.head.appendChild(style);

export default ParticleEffects;